import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { usePreviewContext } from "../../contexts/preview-context";
import { useDefinition } from "../../hooks/useDefinition";
import { useMessage } from "../../hooks/useMessage";
import { useSelectedSpread, useSelectedZone } from "../../hooks/useSelection";
import { Transform } from "../../transform";
import { ZoneSelectionBoxHandlers } from "../zone-selection-box/zone-selection-box-handlers";
import { TransformType, IGuidelineOverlayRef, ILine } from "./guideline-overlay";

const guidelinesState = proxy({
	lines: [] as ILine[],
	handle: ZoneSelectionBoxHandlers.None,
	isZoneMoving: false,
	transformType: TransformType.Move,
	actions: {
		setLines(value: ILine[]) {
			guidelinesState.lines = value;
		},
		setHandle(value: ZoneSelectionBoxHandlers) {
			guidelinesState.handle = value;
		},
		setIsZoneMoving(value: boolean) {
			guidelinesState.isZoneMoving = value;
		},
		setTransformType(value: TransformType) {
			guidelinesState.transformType = value;
		},
	},
});

export function useGuidelineOverlay() {
	const selectedSpread = useSelectedSpread();
	const selectedZone = useSelectedZone();

	const guidelineOverlayRef = useRef<IGuidelineOverlayRef | undefined>(undefined);

	const previewContext = usePreviewContext();

	const definition = useDefinition();

	const { lines } = useSnapshot(guidelinesState);

	const start = useCallback(
		(
			element: SVGSVGElement | undefined,
			drawGuidelines: (
				zone: eXpress.core.IZone | undefined,
				dipToScreenTransform: Transform,
				currentHandle: ZoneSelectionBoxHandlers,
				transformType: TransformType,
			) => ILine[],
			removeGuidelines: () => void,
			currentHandle: ZoneSelectionBoxHandlers,
		) => {
			guidelinesState.actions.setHandle(currentHandle);
			guidelineOverlayRef.current = element
				? {
						el: element,
						drawGuidelines,
						removeGuidelines,
				  }
				: undefined;
		},
		[],
	);

	useMessage(
		useCallback(
			(message) => {
				if (
					message instanceof eXpress.core.SpreadRenderedMessage &&
					guidelinesState.isZoneMoving
				) {
					if (!definition) return;
					const transform = previewContext.getSpreadToScreenTransform(
						selectedSpread,
						undefined,
					);
					if (!transform) return;
					const res = guidelineOverlayRef.current?.drawGuidelines(
						selectedZone,
						transform,
						guidelinesState.handle,
						guidelinesState.transformType,
					);
					const dipProductEdgeRange = eXpress.utils.isNumeric(
						definition.product.edgeSnappingDistanceInMM,
					)
						? eXpress.utils.mmToDip(
								definition.dpi,
								definition.product.edgeSnappingDistanceInMM,
						  )
						: eXpress.utils.mmToDip(definition.dpi, 5);

					if (res) {
						const newLines = res.map((line) => {
							line.lineSegment.maxDist =
								line.type === "page-edge"
									? dipProductEdgeRange
									: eXpress.utils.mmToDip(definition.dpi, 2);
							return line;
						});
						guidelinesState.lines = newLines;
					}
				}
				if (!message.isInteractive) guidelineOverlayRef.current?.removeGuidelines();
			},
			[definition, previewContext, selectedSpread, selectedZone],
		),
		{ captureInteractiveMessages: true },
	);

	return [
		lines,
		useMemo(
			() => ({
				start,
				setHandle: guidelinesState.actions.setHandle,
				setIsZoneMoving: guidelinesState.actions.setIsZoneMoving,
				setTransformType: guidelinesState.actions.setTransformType,
			}),
			[start],
		),
	] as const;
}
